import { __decorate, __metadata } from "tslib";
import { Component, ECSComponent } from '@esengine/ecs-framework';
import { Serializable, Serialize, IgnoreSerialization } from '@esengine/ecs-framework';
import { createDefaultRuntimeState } from './BehaviorTreeData';
import { TaskStatus } from '../Types/TaskStatus';
/**
 * 行为树运行时组件
 *
 * 挂载到游戏Entity上，引用共享的BehaviorTreeData
 * 维护该Entity独立的运行时状态
 */
let BehaviorTreeRuntimeComponent = class BehaviorTreeRuntimeComponent extends Component {
    constructor() {
        super(...arguments);
        /**
         * 引用的行为树资产ID（可序列化）
         */
        this.treeAssetId = '';
        /**
         * 是否自动启动
         */
        this.autoStart = true;
        /**
         * 是否正在运行
         */
        this.isRunning = false;
        /**
         * 节点运行时状态（每个节点独立）
         * 不序列化，每次加载时重新初始化
         */
        this.nodeStates = new Map();
        /**
         * 黑板数据（该Entity独立的数据）
         * 不序列化，通过初始化设置
         */
        this.blackboard = new Map();
        /**
         * 黑板观察者列表
         */
        this.blackboardObservers = new Map();
        /**
         * 当前激活的节点ID列表（用于调试）
         */
        this.activeNodeIds = new Set();
        /**
         * 标记是否需要在下一个tick重置状态
         */
        this.needsReset = false;
        /**
         * 需要中止的节点ID列表
         */
        this.nodesToAbort = new Set();
    }
    /**
     * 获取节点运行时状态
     */
    getNodeState(nodeId) {
        if (!this.nodeStates.has(nodeId)) {
            this.nodeStates.set(nodeId, createDefaultRuntimeState());
        }
        return this.nodeStates.get(nodeId);
    }
    /**
     * 重置节点状态
     */
    resetNodeState(nodeId) {
        const state = this.getNodeState(nodeId);
        state.status = TaskStatus.Invalid;
        state.currentChildIndex = 0;
        delete state.startTime;
        delete state.lastExecutionTime;
        delete state.repeatCount;
        delete state.cachedResult;
        delete state.shuffledIndices;
        delete state.isAborted;
        delete state.lastConditionResult;
        delete state.observedKeys;
    }
    /**
     * 重置所有节点状态
     */
    resetAllStates() {
        this.nodeStates.clear();
        this.activeNodeIds.clear();
    }
    /**
     * 获取黑板值
     */
    getBlackboardValue(key) {
        return this.blackboard.get(key);
    }
    /**
     * 设置黑板值
     */
    setBlackboardValue(key, value) {
        const oldValue = this.blackboard.get(key);
        this.blackboard.set(key, value);
        if (oldValue !== value) {
            this.notifyBlackboardChange(key, value, oldValue);
        }
    }
    /**
     * 检查黑板是否有某个键
     */
    hasBlackboardKey(key) {
        return this.blackboard.has(key);
    }
    /**
     * 初始化黑板（从树定义的默认值）
     */
    initializeBlackboard(variables) {
        if (variables) {
            variables.forEach((value, key) => {
                if (!this.blackboard.has(key)) {
                    this.blackboard.set(key, value);
                }
            });
        }
    }
    /**
     * 清空黑板
     */
    clearBlackboard() {
        this.blackboard.clear();
    }
    /**
     * 启动行为树
     */
    start() {
        this.isRunning = true;
        this.resetAllStates();
    }
    /**
     * 停止行为树
     */
    stop() {
        this.isRunning = false;
        this.activeNodeIds.clear();
    }
    /**
     * 暂停行为树
     */
    pause() {
        this.isRunning = false;
    }
    /**
     * 恢复行为树
     */
    resume() {
        this.isRunning = true;
    }
    /**
     * 注册黑板观察者
     */
    observeBlackboard(nodeId, keys, callback) {
        const observer = {
            nodeId,
            keys: new Set(keys),
            callback
        };
        for (const key of keys) {
            if (!this.blackboardObservers.has(key)) {
                this.blackboardObservers.set(key, []);
            }
            this.blackboardObservers.get(key).push(observer);
        }
    }
    /**
     * 取消注册黑板观察者
     */
    unobserveBlackboard(nodeId) {
        for (const observers of this.blackboardObservers.values()) {
            const index = observers.findIndex(o => o.nodeId === nodeId);
            if (index !== -1) {
                observers.splice(index, 1);
            }
        }
    }
    /**
     * 通知黑板变化
     */
    notifyBlackboardChange(key, newValue, oldValue) {
        const observers = this.blackboardObservers.get(key);
        if (!observers)
            return;
        for (const observer of observers) {
            try {
                observer.callback(key, newValue, oldValue);
            }
            catch (error) {
                console.error(`黑板观察者回调错误 (节点: ${observer.nodeId}):`, error);
            }
        }
    }
    /**
     * 请求中止节点
     */
    requestAbort(nodeId) {
        this.nodesToAbort.add(nodeId);
    }
    /**
     * 检查节点是否需要中止
     */
    shouldAbort(nodeId) {
        return this.nodesToAbort.has(nodeId);
    }
    /**
     * 清除中止请求
     */
    clearAbortRequest(nodeId) {
        this.nodesToAbort.delete(nodeId);
    }
    /**
     * 清除所有中止请求
     */
    clearAllAbortRequests() {
        this.nodesToAbort.clear();
    }
};
__decorate([
    Serialize(),
    __metadata("design:type", String)
], BehaviorTreeRuntimeComponent.prototype, "treeAssetId", void 0);
__decorate([
    Serialize(),
    __metadata("design:type", Boolean)
], BehaviorTreeRuntimeComponent.prototype, "autoStart", void 0);
__decorate([
    IgnoreSerialization(),
    __metadata("design:type", Boolean)
], BehaviorTreeRuntimeComponent.prototype, "isRunning", void 0);
__decorate([
    IgnoreSerialization(),
    __metadata("design:type", Map)
], BehaviorTreeRuntimeComponent.prototype, "nodeStates", void 0);
__decorate([
    IgnoreSerialization(),
    __metadata("design:type", Map)
], BehaviorTreeRuntimeComponent.prototype, "blackboard", void 0);
__decorate([
    IgnoreSerialization(),
    __metadata("design:type", Map)
], BehaviorTreeRuntimeComponent.prototype, "blackboardObservers", void 0);
__decorate([
    IgnoreSerialization(),
    __metadata("design:type", Set)
], BehaviorTreeRuntimeComponent.prototype, "activeNodeIds", void 0);
__decorate([
    IgnoreSerialization(),
    __metadata("design:type", Boolean)
], BehaviorTreeRuntimeComponent.prototype, "needsReset", void 0);
__decorate([
    IgnoreSerialization(),
    __metadata("design:type", Set)
], BehaviorTreeRuntimeComponent.prototype, "nodesToAbort", void 0);
BehaviorTreeRuntimeComponent = __decorate([
    ECSComponent('BehaviorTreeRuntime'),
    Serializable({ version: 1 })
], BehaviorTreeRuntimeComponent);
export { BehaviorTreeRuntimeComponent };
//# sourceMappingURL=BehaviorTreeRuntimeComponent.js.map